import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, RefreshCw, Sparkles, Check, Loader2, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const commitKeyFor = (dateStr) => `niko_wisdom_commit_${dateStr}`;
const toKey = (d) => d.toISOString().slice(0, 10);

// Build the last N days ending today (inclusive), oldest first
const lastNDays = (n) => {
  const out = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push(d);
  }
  return out;
};

// Count consecutive committed days ending today
const computeStreak = () => {
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 365; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    if (localStorage.getItem(commitKeyFor(toKey(d))) === "1") streak += 1;
    else break;
  }
  return streak;
};

/**
 * Daily Leadership Wisdom — Dashboard micro-card.
 * Fetches today's wisdom from /api/wisdom/daily. Lets the user "try this today"
 * (stores a local flag so they can feel the win) and reshuffle to see another.
 * Includes a 7-day habit strip + streak count (localStorage only, privacy-friendly).
 */
export default function DailyWisdomCard() {
  const [wisdom, setWisdom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [commit, setCommit] = useState(false);
  const [streak, setStreak] = useState(0);
  const [last7, setLast7] = useState([]);

  const todayKey = toKey(new Date());
  const commitKey = commitKeyFor(todayKey);

  const refreshHabit = useCallback(() => {
    setCommit(localStorage.getItem(commitKey) === "1");
    setStreak(computeStreak());
    setLast7(lastNDays(7).map((d) => ({
      key: toKey(d),
      label: d.toLocaleDateString("en-US", { weekday: "narrow" }),
      committed: localStorage.getItem(commitKeyFor(toKey(d))) === "1",
      isToday: toKey(d) === todayKey,
    })));
  }, [commitKey, todayKey]);

  const fetchWisdom = useCallback(async (opts = {}) => {
    setLoading(true);
    setErr("");
    try {
      const params = opts.date ? { date: opts.date } : {};
      // Default: let Niko match today's wisdom to today's threat profile.
      // `opts.rotation=true` (used by the "Show another" button) bypasses context.
      if (!opts.rotation && !opts.date) {
        params.contextual = true;
      }
      const res = await axios.get(`${API}/wisdom/daily`, {
        params,
        withCredentials: true,
      });
      setWisdom(res.data);
    } catch (e) {
      setErr("Couldn't load today's wisdom.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWisdom();
    refreshHabit();
  }, [fetchWisdom, refreshHabit]);

  // "Show another" samples a different day in the library (not today) for variety
  const shuffle = () => {
    if (!wisdom?.total_wisdoms) return fetchWisdom({ rotation: true });
    let offset = Math.floor(Math.random() * wisdom.total_wisdoms);
    if (offset === 0) offset = 1;
    const d = new Date();
    d.setDate(d.getDate() + offset);
    fetchWisdom({ date: toKey(d) });
  };

  const toggleCommit = () => {
    const next = !commit;
    if (next) localStorage.setItem(commitKey, "1");
    else localStorage.removeItem(commitKey);
    refreshHabit();
  };

  return (
    <Card className="bento-card border-purple-500/30" data-testid="daily-wisdom-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-purple-400" />
            Daily Leadership Wisdom
          </span>
          <div className="flex items-center gap-3">
            {wisdom?.context_matched && wisdom?.matched_theme && (
              <span
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/40 text-purple-200 font-bold"
                data-testid="wisdom-theme-chip"
                title="Matched to today's threat profile"
              >
                {wisdom.matched_theme}
              </span>
            )}
            {streak > 0 && (
              <span
                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs font-semibold"
                data-testid="wisdom-streak-badge"
                title={`You've followed through ${streak} ${streak === 1 ? "day" : "days"} in a row`}
              >
                <Flame className="w-3.5 h-3.5" /> {streak}
              </span>
            )}
            {wisdom?.total_wisdoms && (
              <span className="text-[10px] uppercase tracking-wide text-purple-300/80 font-semibold">
                Day {wisdom.day_number} · {wisdom.total_wisdoms} in library
              </span>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading today's habit...
          </div>
        )}

        {!loading && err && (
          <div className="text-sm text-red-400" data-testid="wisdom-error">{err}</div>
        )}

        {!loading && wisdom && (
          <div className="space-y-3">
            <div className="text-base font-semibold leading-snug text-white" data-testid="wisdom-tip">
              {wisdom.tip}
            </div>

            <div className="text-sm text-zinc-300 leading-relaxed">
              <span className="text-purple-300 font-semibold">Today's move: </span>
              {wisdom.action}
            </div>

            <div className="text-sm text-zinc-300 italic leading-relaxed border-l-2 border-purple-500/50 pl-3">
              <span className="not-italic text-purple-300 font-semibold">Try saying: </span>
              "{wisdom.influence_line}"
            </div>

            {/* 7-day habit strip */}
            <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
              <div className="flex items-center gap-1.5" data-testid="wisdom-habit-strip">
                {last7.map((d) => (
                  <div
                    key={d.key}
                    className="flex flex-col items-center gap-1"
                    title={`${d.key}${d.committed ? " — committed" : ""}`}
                  >
                    <div
                      className={[
                        "w-5 h-5 rounded-full flex items-center justify-center transition-colors border",
                        d.committed
                          ? "bg-emerald-500/90 border-emerald-400 text-white"
                          : "bg-zinc-900 border-zinc-700 text-zinc-600",
                        d.isToday ? "ring-2 ring-purple-500/70 ring-offset-1 ring-offset-zinc-950" : "",
                      ].join(" ")}
                      data-testid={`wisdom-day-${d.key}${d.isToday ? "-today" : ""}`}
                    >
                      {d.committed ? <Check className="w-3 h-3" /> : null}
                    </div>
                    <span className={`text-[10px] ${d.isToday ? "text-purple-300 font-semibold" : "text-zinc-500"}`}>
                      {d.label}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={shuffle}
                  className="bg-zinc-900 border-zinc-700 text-xs hover:bg-zinc-800"
                  data-testid="wisdom-shuffle-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />
                  Show another
                </Button>
                <Button
                  size="sm"
                  onClick={toggleCommit}
                  className={commit
                    ? "bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                    : "bg-purple-600 hover:bg-purple-500 text-white text-xs"
                  }
                  data-testid="wisdom-commit-btn"
                >
                  {commit ? (
                    <><Check className="w-3.5 h-3.5 mr-1" />Committed today</>
                  ) : (
                    <><Sparkles className="w-3.5 h-3.5 mr-1" />Try this today</>
                  )}
                </Button>
              </div>
            </div>

            <div className="text-xs text-zinc-400 pt-1">
              — <span className="text-zinc-300">{wisdom.book}</span>, {wisdom.author}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
